/**
 * Requests that arrived since the last planning, and the wrap-up: the plan
 * read back, with every gap in red, plus copy-as-text and the outcome file.
 */

import { useState } from 'react'
import {
  Button,
  Caption1,
  ToggleButton,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowDownload20Regular,
  CalendarLtr20Regular,
  CheckmarkCircle20Regular,
  Clock20Regular,
  Copy20Regular,
  Flag20Regular,
  Gavel20Regular,
  Lightbulb20Regular,
  Person16Regular,
  PeopleTeam16Regular,
  Target20Regular,
  Wrench20Regular,
} from '@fluentui/react-icons'
import {
  DECISIONS,
  activeCardsIn,
  colName,
  goalOf,
  isHouse,
  isCompleted,
  outcomePlan,
  pickOf,
  refsText,
  summaryText,
  whoOf,
} from '../../lib/planning'
import { downloadBlob } from '../../lib/download'
import type { Plan, PlanEdit, PlanScopeItem, PlanState } from '../../types/planning'
import { InfoHint, IssueLink, PlanChip, usePanelStyles } from './PlanParts'

const useStyles = makeStyles({
  reqs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  reqTop: { display: 'flex', alignItems: 'center', gap: '6px' },
  role: { display: 'inline-flex', alignItems: 'center', gap: '4px', flexGrow: 1 },
  h4: { margin: 0, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold },
  p: { margin: 0 },
  seg: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  full: { gridColumn: '1 / -1' },
  goal: { margin: 0, fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  actions: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
})

export function RequestList({
  plan,
  state,
  edit,
}: {
  plan: Plan
  state: PlanState
  edit: (e: PlanEdit) => void
}) {
  const styles = useStyles()
  const panel = usePanelStyles()
  return (
    <div className={styles.reqs}>
      {(plan.requests ?? []).map((r) => (
        <article key={r.id} className={panel.panel} data-testid={`req-${r.id}`} tabIndex={-1}>
          <div className={styles.reqTop}>
            <Caption1 className={styles.role}>
              <Person16Regular aria-hidden="true" />
              {r.role}
            </Caption1>
            {r.story && <InfoHint text={`Original: ${r.story}`} />}
            <PlanChip chip={r.kind} />
          </div>
          <h4 className={styles.h4}>{r.t}</h4>
          {r.so && <p className={styles.p}>{r.so}</p>}
          {(r.where ?? []).length > 0 && (
            <p className={styles.p}>
              <b>Where:</b>{' '}
              {(r.where ?? []).map(([n, l], i) => (
                <span key={n}>
                  {i > 0 && ' · '}
                  <IssueLink n={n} base={plan.repo_url} /> {l}
                </span>
              ))}
            </p>
          )}
          {r.note && <Caption1 className={panel.muted}>{r.note}</Caption1>}
          <div className={styles.seg} role="group" aria-label={`Decision for ${r.t}`}>
            {DECISIONS.map(([k, label]) => (
              <ToggleButton
                key={k}
                size="small"
                checked={state.reqs?.[r.id] === k}
                onClick={() =>
                  edit({
                    op: 'set',
                    path: ['reqs', r.id],
                    value: state.reqs?.[r.id] === k ? null : k,
                  })
                }
              >
                {label}
              </ToggleButton>
            ))}
          </div>
        </article>
      ))}
    </div>
  )
}

export function PlanWrapUp({ plan, state }: { plan: Plan; state: PlanState }) {
  const styles = useStyles()
  const panel = usePanelStyles()
  const [copied, setCopied] = useState('')
  const goal = goalOf(plan, state)
  const picks = (plan.ideas ?? []).filter((x) => pickOf(x.id, state).on)
  const house = (plan.scope ?? []).filter((c) => isHouse(c) && !isCompleted(c))
  const completed = (plan.scope ?? []).filter(isCompleted)
  const parking = state.parking ?? []
  const said = state.said ?? []

  const missing = (text: string) => <span className={panel.missing}>{text}</span>
  const item = (c: PlanScopeItem) => (
    <li key={c.refs.join('-')}>
      {refsText(c)} {c.t} · {whoOf(c, state) || missing('no owner')}
    </li>
  )
  const list = (items: PlanScopeItem[]) =>
    items.length ? <ul className={panel.list}>{items.map(item)}</ul> : <Caption1>None.</Caption1>

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summaryText(plan, state))
      setCopied('Copied.')
    } catch {
      setCopied("Couldn't copy. Download outcome still works.")
    }
    setTimeout(() => setCopied(''), 4000)
  }
  const download = () => {
    const blob = new Blob([JSON.stringify(outcomePlan(plan, state), null, 2)], {
      type: 'application/json',
    })
    downloadBlob(`${plan.cycle}.json`, blob)
  }

  return (
    <>
      <div className={styles.grid}>
        <div className={mergeClasses(panel.panel, styles.full)}>
          <div className={panel.panelHead}>
            <Target20Regular aria-hidden="true" />
            Goal
          </div>
          <p className={styles.goal}>{goal || missing('No goal yet')}</p>
        </div>
        <div className={panel.panel}>
          <div className={panel.panelHead}>
            <Flag20Regular aria-hidden="true" />
            Must
          </div>
          {list(activeCardsIn(plan, state, 'must'))}
        </div>
        {((plan.questions ?? []).length > 0 || said.length > 0) && (
          <div className={mergeClasses(panel.panel, styles.full)}>
            <div className={panel.panelHead}>
              <Gavel20Regular aria-hidden="true" />
              Decisions
            </div>
            <ul className={panel.list}>
              {(plan.questions ?? []).map((q) => (
                <li key={q.id}>
                  #{q.id} {q.t} · {state.out?.[String(q.id)] || missing('undecided')}
                </li>
              ))}
              {said.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          </div>
        )}
        {(plan.ideas ?? []).length > 0 && (
          <div className={panel.panel}>
            <div className={panel.panelHead}>
              <Lightbulb20Regular aria-hidden="true" />
              Ideas to try, two weeks
            </div>
            {picks.length ? (
              <ul className={panel.list}>
                {picks.map((x) => {
                  const p = pickOf(x.id, state)
                  return (
                    <li key={x.id}>
                      #{x.id} {x.t} · runs: {p.who.trim() || missing('who?')} · success:{' '}
                      {p.ok.trim() || missing('what counts?')}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <Caption1>None.</Caption1>
            )}
          </div>
        )}
        <div className={panel.panel}>
          <div className={panel.panelHead}>
            <Clock20Regular aria-hidden="true" />
            If time allows
          </div>
          {list(activeCardsIn(plan, state, 'maybe'))}
        </div>
        <div className={panel.panel}>
          <div className={panel.panelHead}>
            <CalendarLtr20Regular aria-hidden="true" />
            {colName(plan, 'later')} · notes for later
          </div>
          {list(activeCardsIn(plan, state, 'later'))}
          {parking.length > 0 && (
            <ul className={panel.list}>
              {parking.map((p) => (
                <li key={p}>Note: {p}</li>
              ))}
            </ul>
          )}
        </div>
        {(plan.requests ?? []).length > 0 && (
          <div className={mergeClasses(panel.panel, styles.full)}>
            <div className={panel.panelHead}>
              <PeopleTeam16Regular aria-hidden="true" />
              {plan.requests_title || 'Requests'}
            </div>
            <ul className={panel.list}>
              {(plan.requests ?? []).map((r) => (
                <li key={r.id}>
                  {r.t} ·{' '}
                  {DECISIONS.find((d) => d[0] === state.reqs?.[r.id])?.[1] ?? missing('undecided')}
                </li>
              ))}
            </ul>
          </div>
        )}
        {completed.length > 0 && (
          <div className={mergeClasses(panel.panel, styles.full)}>
            <div className={panel.panelHead}>
              <CheckmarkCircle20Regular aria-hidden="true" />
              Completed (context only)
            </div>
            <ul className={panel.list}>
              {completed.map((c) => <li key={c.refs.join('-')}>{refsText(c)} {c.t}</li>)}
            </ul>
          </div>
        )}
        {house.length > 0 && (
          <div className={mergeClasses(panel.panel, styles.full)}>
            <div className={panel.panelHead}>
              <Wrench20Regular aria-hidden="true" />
              Housekeeping · as time allows
            </div>
            {/* One item a line, like the other panels: a single 12 px line
                could not be read projected (#1578). */}
            <ul className={panel.list}>
              {house.map((c) => (
                <li key={c.refs.join('-')}>
                  {refsText(c)} {c.t}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className={styles.actions}>
        <Button appearance="primary" icon={<Copy20Regular />} onClick={copy}>
          Copy as text
        </Button>
        <Button icon={<ArrowDownload20Regular />} onClick={download}>
          Download outcome
        </Button>
        <InfoHint text="The draft with the room's decisions written in. Feed it to plan.py snapshot to record the agreed plan." />
        <Caption1 aria-live="polite">{copied}</Caption1>
      </div>
    </>
  )
}
